/**
 * Product Card Colour Carousel
 * Handles navigation through colour swatches, showing 5 at a time
 */

// Prevent duplicate class declaration
if (!window.ProductCardCarousel) {
  window.ProductCardCarousel = class ProductCardCarousel {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.track = wrapper.querySelector('.product-card__colour-swatches-track');
    this.prevBtn = wrapper.querySelector('.product-card__carousel-arrow--prev');
    this.nextBtn = wrapper.querySelector('.product-card__carousel-arrow--next');
    this.swatches = this.track ? Array.from(this.track.querySelectorAll('.product-card__colour-swatch')) : [];
    
    this.isMobile = window.matchMedia('(max-width: 749px)').matches;
    this.currentIndex = 0;
    // Desktop: show as many swatches as fit (computed from available width)
    this.itemsPerPage = 6; // Default, will be recalculated based on available space
    
    // Defaults (will be measured on desktop)
    this.itemWidth = this.isMobile ? 40 : 45;
    this.gap = this.isMobile ? 8 : 12;
    
    // Bind handlers so we can remove listeners on re-init
    this.onPrevClick = (e) => {
      e.preventDefault();
      this.prev();
    };
    this.onNextClick = (e) => {
      e.preventDefault();
      this.next();
    };
    this.onSwatchClick = (e) => {
      this.handleSwatchClick(e);
    };

    this.init();
  }
  
  init() {
    // Always set up swatch click handlers first
    this.setupSwatchClickHandlers();
    
    // MOBILE: Use native horizontal scroll (CSS) instead of translateX carousel.
    // Keep desktop arrow-carousel behaviour unchanged.
    if (this.isMobile) {
      if (this.track) this.track.style.transform = '';
      return;
    }

    // Desktop: only set up carousel navigation if arrows exist
    if (this.prevBtn && this.nextBtn) {
      // Desktop: measure actual swatch size + CSS gap so translation is always correct
      this.recalcLayout();
      
      this.prevBtn.addEventListener('click', this.onPrevClick);
      this.nextBtn.addEventListener('click', this.onNextClick);
      
      this.updateButtons();

      // Recalc on resize to keep movement correct if cards reflow
      this.onResize = () => {
        if (!this.track || !this.swatches || this.swatches.length === 0) return;
        this.recalcLayout();
        // Clamp current index after recalculating
        const maxIndex = Math.max(0, this.swatches.length - this.itemsPerPage);
        if (this.currentIndex > maxIndex) this.currentIndex = maxIndex;
        this.updatePosition();
      };
      if (window && typeof window.addEventListener === 'function') {
        window.addEventListener('resize', this.onResize);
      }
    }
  }
  
  setupSwatchClickHandlers() {
    // Add click handlers to all swatches to update the product card image
    if (!this.swatches || this.swatches.length === 0) return;
    this.swatches.forEach(swatch => {
      if (!swatch) return;
      // Remove any existing handler to prevent duplicates
      swatch.removeEventListener('click', this.onSwatchClick);
      swatch.addEventListener('click', this.onSwatchClick);
    });
  }
  
  handleSwatchClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const swatch = e.currentTarget;
    const productId = swatch.getAttribute('data-product-id');
    
    if (!productId) {
      console.warn('No product ID found on swatch');
      return;
    }
    
    // Find the product card wrapper (closest ancestor)
    const cardWrapper = swatch.closest('.product-card-wrapper');
    if (!cardWrapper) {
      console.warn('No card wrapper found');
      return;
    }
    
    // Find the main product image
    const mainImage = cardWrapper.querySelector(`img[data-product-card-image="${productId}"]`);
    if (!mainImage) {
      console.warn(`No main image found for product ID: ${productId}`);
      return;
    }
    
    // Get variant image data from the clicked swatch (trim whitespace)
    const srcset = swatch.getAttribute('data-variant-image-srcset')?.trim();
    const src = swatch.getAttribute('data-variant-image-src')?.trim();
    const alt = swatch.getAttribute('data-variant-image-alt')?.trim();
    const width = swatch.getAttribute('data-variant-image-width')?.trim();
    const height = swatch.getAttribute('data-variant-image-height')?.trim();
    
    if (!src || !srcset) {
      console.warn('Missing src or srcset:', { src, srcset });
      return;
    }
    
    // Update the main image
    mainImage.srcset = srcset;
    mainImage.src = src;
    if (alt) mainImage.alt = alt;
    if (width) mainImage.width = width;
    if (height) mainImage.height = height;
    
    // Also update the secondary image (shown on hover) to the selected color
    const secondaryImage = cardWrapper.querySelector(`img[data-product-card-secondary-image="${productId}"]`);
    if (secondaryImage) {
      secondaryImage.srcset = srcset;
      secondaryImage.src = src;
      if (alt) secondaryImage.alt = alt;
      if (width) secondaryImage.width = width;
      if (height) secondaryImage.height = height;
    }
    
    // Mark that a color has been selected
    cardWrapper.setAttribute('data-color-selected', 'true');
    
    // Update active swatch state (optional visual feedback)
    const allSwatches = cardWrapper.querySelectorAll('.product-card__colour-swatch');
    allSwatches.forEach(s => s.classList.remove('is-active'));
    swatch.classList.add('is-active');
  }
  
  recalcLayout() {
    if (this.isMobile) return;
    if (!this.track || this.swatches.length === 0) return;

    const swatchEl = this.swatches[0];
    const swatchRect = swatchEl.getBoundingClientRect();
    const measuredItemWidth = Math.round(swatchRect.width);

    // Read flex gap from the track (e.g. 0.75rem on desktop)
    const cs = window.getComputedStyle(this.track);
    const gapStr = cs.gap || cs.columnGap || '0px';
    const measuredGap = Math.round(parseFloat(gapStr)) || 0;

    // Only apply if sane
    if (measuredItemWidth > 0) this.itemWidth = measuredItemWidth;
    this.gap = measuredGap;

    // Compute how many fit in the viewport (no artificial cap, use available space)
    const viewport = this.wrapper.querySelector('.product-card__colour-swatches');
    if (viewport) {
      const w = viewport.getBoundingClientRect().width;
      const denom = this.itemWidth + this.gap;
      if (w > 0 && denom > 0) {
        const fit = Math.floor((w + this.gap) / denom);
        // Allow up to 8 items to fit more swatches between arrows
        this.itemsPerPage = Math.max(1, Math.min(8, fit));
      }
    }
  }

  destroy() {
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrevClick);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNextClick);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.track) this.track.style.transform = '';
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updatePosition();
    }
  }
  
  next() {
    const maxIndex = Math.max(0, this.swatches.length - this.itemsPerPage);
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
      this.updatePosition();
    }
  }
  
  updatePosition() {
    if (this.isMobile) return;
    const offset = Math.round(this.currentIndex * (this.itemWidth + this.gap));
    this.track.style.transform = `translateX(-${offset}px)`;
    this.updateButtons();
  }
  
  updateButtons() {
    if (!this.prevBtn || !this.nextBtn) return;
    
    // Disable prev button if at start
    this.prevBtn.disabled = this.currentIndex === 0;
    
    // Disable next button if at end
    const maxIndex = Math.max(0, this.swatches.length - this.itemsPerPage);
    this.nextBtn.disabled = this.currentIndex >= maxIndex;
  }
  };
}

// Standalone function to set up swatch click handlers
function setupSwatchClickHandlers(wrapper) {
  if (!wrapper) return;
  const track = wrapper.querySelector('.product-card__colour-swatches-track');
  if (!track) return;
  
  const swatches = Array.from(track.querySelectorAll('.product-card__colour-swatch'));
  if (!swatches || swatches.length === 0) return;
  
  swatches.forEach(swatch => {
    // Remove any existing handler to prevent duplicates
    const existingHandler = swatch.__swatchClickHandler;
    if (existingHandler) {
      swatch.removeEventListener('click', existingHandler);
    }
    
    // Create new handler
    const handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const productId = swatch.getAttribute('data-product-id');
      if (!productId) {
        console.warn('No product ID found on swatch');
        return;
      }
      
      // Find the product card wrapper (closest ancestor)
      const cardWrapper = swatch.closest('.product-card-wrapper');
      if (!cardWrapper) {
        console.warn('No card wrapper found');
        return;
      }
      
      // Find the main product image
      const mainImage = cardWrapper.querySelector(`img[data-product-card-image="${productId}"]`);
      if (!mainImage) {
        console.warn(`No main image found for product ID: ${productId}`);
        return;
      }
      
      // Get variant image data from the clicked swatch (trim whitespace)
      const srcset = swatch.getAttribute('data-variant-image-srcset')?.trim();
      const src = swatch.getAttribute('data-variant-image-src')?.trim();
      const alt = swatch.getAttribute('data-variant-image-alt')?.trim();
      const width = swatch.getAttribute('data-variant-image-width')?.trim();
      const height = swatch.getAttribute('data-variant-image-height')?.trim();
      
      if (!src || !srcset) {
        console.warn('Missing src or srcset:', { src, srcset });
        return;
      }
      
      // Update the main image
      mainImage.srcset = srcset;
      mainImage.src = src;
      if (alt) mainImage.alt = alt;
      if (width) mainImage.width = width;
      if (height) mainImage.height = height;
      
      // Also update the secondary image (shown on hover) to the selected color
      const secondaryImage = cardWrapper.querySelector(`img[data-product-card-secondary-image="${productId}"]`);
      if (secondaryImage) {
        secondaryImage.srcset = srcset;
        secondaryImage.src = src;
        if (alt) secondaryImage.alt = alt;
        if (width) secondaryImage.width = width;
        if (height) secondaryImage.height = height;
      }
      
      // Mark that a color has been selected
      cardWrapper.setAttribute('data-color-selected', 'true');
      
      // Update active swatch state (optional visual feedback)
      const allSwatches = cardWrapper.querySelectorAll('.product-card__colour-swatch');
      allSwatches.forEach(s => s.classList.remove('is-active'));
      swatch.classList.add('is-active');
    };
    
    // Store handler reference for cleanup
    swatch.__swatchClickHandler = handler;
    swatch.addEventListener('click', handler);
  });
}

// Initialize all carousels on the page (only if not already initialized)
if (!window.productCardCarouselInitialized) {
  window.productCardCarouselInitialized = true;
  
  function initCarousels() {
    if (!document) return;
    const mode = window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
    const carouselWrappers = document.querySelectorAll('.product-card__colour-carousel-wrapper');
    if (!carouselWrappers || carouselWrappers.length === 0) return;

    carouselWrappers.forEach(wrapper => {
      if (!wrapper) return;
      const currentMode = wrapper.getAttribute('data-carousel-mode');
      const hasArrows = wrapper.querySelector('.product-card__carousel-arrow');

      // Tag for CSS targeting when no arrows are present
      if (!hasArrows) wrapper.classList.add('product-card__no-arrows');

      // If mode hasn't changed and we already initialised for this mode, skip
      if (wrapper.getAttribute('data-carousel-initialized') === 'true' && currentMode === mode) return;

      // Tear down any previous instance
      if (wrapper.__carouselInstance && typeof wrapper.__carouselInstance.destroy === 'function') {
        wrapper.__carouselInstance.destroy();
      }
      wrapper.__carouselInstance = null;

      wrapper.setAttribute('data-carousel-initialized', 'true');
      wrapper.setAttribute('data-carousel-mode', mode);

      if (mode === 'desktop' && hasArrows) {
        wrapper.__carouselInstance = new window.ProductCardCarousel(wrapper);
      } else {
        // Mobile or desktop without arrows: ensure transform is cleared and set up handlers
        const track = wrapper.querySelector('.product-card__colour-swatches-track');
        if (track) track.style.transform = '';
        setupSwatchClickHandlers(wrapper);
      }
    });
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }

  // Reinitialize after Shopify section renders (for quick view modals, etc.)
  if (typeof Shopify !== 'undefined') {
    document.addEventListener('shopify:section:load', initCarousels);
  }

  // Reinitialize when crossing the mobile/desktop breakpoint (e.g. responsive devtools)
  const mq = window.matchMedia('(max-width: 749px)');
  if (mq && typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', initCarousels);
  } else if (mq && typeof mq.addListener === 'function') {
    mq.addListener(initCarousels);
  }
}

